import * as d3 from "d3";
import { useEffect, useRef } from "react";
import type { RollingAverage } from "../../../../../../apiClient/generated";
import { useResizeObserver } from "../../../../../../ui/Plot/hooks";
import { computePlotTicks, TIMELINE_MARGINS } from "../plot";

const SERIES_CONFIG = [
    {
        key: "dailyRollupAverageKghr" as const,
        label: "Daily Average",
        color: "#3b82f6",
    },
    {
        key: "sevenDayRollupAverageKghr" as const,
        label: "7-Day Average",
        color: "#f59e0b",
    },
    {
        key: "ninetyDayRollupAverageKghr" as const,
        label: "90-Day Average",
        color: "#10b981",
    },
];

interface RollingAveragesPlotProps {
    data: RollingAverage[];
    dataDateRange: {
        dateBefore: Date;
        dateAfter: Date;
    };
    setHoverContext?: (data?: { x: Date; y: number }) => void;
}

export const RollingAveragesPlot = ({
    data,
    dataDateRange,
    setHoverContext,
}: RollingAveragesPlotProps) => {
    const svgRef = useRef<SVGSVGElement>();
    const containerRef = useRef<HTMLDivElement>();
    const dimensions = useResizeObserver(containerRef);

    useEffect(() => {
        if (!dimensions.width || !dimensions.height || data.length === 0)
            return;

        const margin = {
            ...TIMELINE_MARGINS,
            bottom: 12,
            top: 12,
        };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        // Clear previous
        d3.select(svgRef.current).selectAll("*").remove();

        const svg = d3
            .select(svgRef.current)
            .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`)
            .attr("width", dimensions.width)
            .attr("height", dimensions.height);

        const chart = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        chart
            .append("rect")
            .attr("width", width)
            .attr("height", height)
            .attr("fill", "transparent");

        // Clip path
        const clipId = `rolling-avg-clip-${Math.random().toString(36).substr(2, 9)}`;
        svg.append("defs")
            .append("clipPath")
            .attr("id", clipId)
            .append("rect")
            .attr("x", 0)
            .attr("y", 0)
            .attr("width", width)
            .attr("height", height);

        // Sort data by date
        const sortedData = [...data].sort(
            (a, b) => a.reportDateUtc.getTime() - b.reportDateUtc.getTime(),
        );

        // X Axis
        const dateRange = [dataDateRange.dateAfter, dataDateRange.dateBefore];
        const xScale = d3.scaleUtc().domain(dateRange).range([0, width]);

        const { tickFormat, tickValues } = computePlotTicks({
            startDate: dataDateRange.dateAfter,
            endDate: dataDateRange.dateBefore,
            plotWidth: width,
        });

        chart
            .append("g")
            .attr("transform", `translate(0,${height - margin.bottom})`)
            .call(
                d3
                    .axisBottom(xScale)
                    .tickValues(tickValues)
                    .tickSize(5)
                    .tickSizeOuter(0)
                    .tickPadding(2)
                    .tickFormat(tickFormat),
            )
            .selectAll("text")
            .attr("font-size", "12px")
            .attr("fill", "#777");

        // Grid lines x
        chart
            .append("g")
            .attr("class", "grid")
            .attr("transform", `translate(0,${height - margin.bottom})`)
            .call(
                d3
                    .axisBottom(xScale)
                    .tickValues(tickValues)
                    .tickSize(-height + margin.top + margin.bottom)
                    .tickSizeOuter(1)
                    .tickFormat(() => ""),
            )
            .selectAll("line")
            .style("stroke", "#e5e7eb")
            .style("stroke-dasharray", "6,6");

        // Y Axis
        const allValues = sortedData.flatMap((d) =>
            SERIES_CONFIG.map((s) => d[s.key]).filter(
                (v) => v != null && Number.isFinite(v),
            ),
        );
        const yMax = d3.max(allValues) || 10;

        const y = d3
            .scaleLinear()
            .domain([0, yMax])
            .nice()
            .range([height - margin.bottom, margin.top]);

        // Grid lines y
        chart
            .append("g")
            .attr("class", "grid")
            .call(
                d3
                    .axisLeft(y)
                    .ticks(5)
                    .tickSize(-width)
                    .tickFormat(() => ""),
            )
            .call((g) => g.select(".domain").remove())
            .selectAll("line")
            .style("stroke", "#e5e7eb")
            .style("stroke-width", 1)
            .style("stroke-dasharray", "6,6");

        chart.append("g").attr("class", "y-axis").call(d3.axisLeft(y).ticks(5));

        // Y-axis label
        svg.append("text")
            .classed("text-gray-500", true)
            .attr("transform", `rotate(-90) translate(${-height / 2}, 15)`)
            .attr("x", 0)
            .attr("y", 0)
            .attr("text-anchor", "middle")
            .style("font-size", "14px")
            .text("kg/hr (methane)");

        // Plot group with clipping
        const plotGroup = chart
            .append("g")
            .attr("clip-path", `url(#${clipId})`);

        // Draw lines for each series
        SERIES_CONFIG.forEach((series) => {
            const lineData = sortedData
                .filter(
                    (d) =>
                        d[series.key] != null && Number.isFinite(d[series.key]),
                )
                .map((d) => ({
                    date: d.reportDateUtc,
                    value: d[series.key] as number,
                }));

            if (lineData.length === 0) return;

            const line = d3
                .line<{ date: Date; value: number }>()
                .x((d) => xScale(d.date))
                .y((d) => y(d.value));

            plotGroup
                .append("path")
                .datum(lineData)
                .attr("fill", "none")
                .attr("stroke", series.color)
                .attr("stroke-width", 1.5)
                .attr("d", line);

            // Draw points
            plotGroup
                .selectAll(`.points-${series.key}`)
                .data(lineData)
                .join("circle")
                .attr("class", `points-${series.key}`)
                .attr("cx", (d) => xScale(d.date))
                .attr("cy", (d) => y(d.value))
                .attr("r", 2.5)
                .attr("fill", series.color)
                .attr("stroke", "white")
                .attr("stroke-width", 1);
        });

        // Hover events
        const containerRect = containerRef.current.getBoundingClientRect();
        chart
            .on("mousemove", (event) => {
                const rawX = xScale.invert(
                    event.clientX - containerRect.left - margin.left,
                );
                if (setHoverContext) {
                    setHoverContext({
                        x: rawX,
                        y: y.invert(
                            event.clientY - containerRect.top - margin.top,
                        ),
                    });
                }
            })
            .on("mouseout", () => {
                if (setHoverContext) {
                    setHoverContext(undefined);
                }
            });

        // Legend
        const legend = chart
            .append("g")
            .attr("class", "legend")
            .attr("transform", `translate(0, ${-5})`);

        let xOffset = 0;
        SERIES_CONFIG.forEach((series) => {
            const legendItem = legend
                .append("g")
                .attr("transform", `translate(${xOffset}, 0)`);

            legendItem
                .append("rect")
                .attr("width", 12)
                .attr("height", 12)
                .attr("fill", series.color)
                .attr("fill-opacity", 1)
                .attr("stroke", series.color)
                .attr("stroke-width", 1);

            legendItem
                .append("text")
                .attr("x", 18)
                .attr("y", 10)
                .attr("font-size", "12px")
                .attr("fill", "#333")
                .text(series.label);

            xOffset += 18 + series.label.length * 7 + 15;
        });
    }, [data, dimensions, dataDateRange, setHoverContext]);

    return (
        <div ref={containerRef} className="w-full h-64 relative">
            <svg ref={svgRef} style={{ width: "100%", height: "100%" }}></svg>
        </div>
    );
};
